import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@/components/UI/DataTable';
import Modal from '@/components/UI/Modal';
import { getDate } from '@einhaus/meteoric-helpers';
import { summarizeLegUnderlyingBias } from '@einhaus/project314-shared';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { BrokerageOrderConfirmation, EntityObjectCompressed, EntityType } from '@einhaus/project314-types';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import Button from '@/components/UI/Button.tsx';
import { DeleteButton } from '@/components/UI';
import EntityLink from '@/components/Entity/EntityLink.tsx';
import EntryLogLink from '@/components/EntryLog/EntryLogLink.tsx';
import { trpc } from '@/api/trpc.ts';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { formatPositionSide, formatStrategyLegSemantics, formatUnderlyingBias } from '@/utils/tradeSemantics';

type FormattedTrade = {
    id: number;
    entityId: number;
    entityType: EntityType;
    strategyId: number;
    direction: string;
    underlyingBias: string;
    entryLogId: number | null;
    insertDateTime: string;
    entity: string | React.ReactNode;
    legs: React.ReactNode;
    strategy: string;
    entryLog: string | React.ReactNode;
    date: string;
    actions: string;
    actionComplete: string | null;
    /** Existing trade ids this entry will close on approval (governance replacement), if any. */
    replaces: number[];
};

const PortfolioPendingTrades: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const portfolioId = id ? parseInt(id, 10) : 0;

    const [error, setError] = useState<string | null>(null);
    // Local overlay marking which trades the user has approved/rejected this session (applied to derived rows).
    const [actionCompleteById, setActionCompleteById] = useState<Record<number, 'approved' | 'rejected'>>({});
    const [actionInProgress, setActionInProgress] = useState<Record<number, boolean>>({});

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // Modal state
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const [tradeConfirmation, setTradeConfirmation] = useState<{
        tradeId: number;
        entryLogId: number;
        confirmations: BrokerageOrderConfirmation[];
    } | null>(null);

    // Fetch portfolio with tRPC
    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({ id: portfolioId, shouldFetchTrades: true }),
            enabled: portfolioId > 0
        },
        {
            fallbackErrorMessage: 'Failed to load portfolio details',
            setError: (msg) => setError(msg)
        }
    );

    // Extract portfolio data
    const portfolio = useMemo(
        () => (portfolioQuery.data?.portfolios && portfolioQuery.data.portfolios.length > 0 ? portfolioQuery.data.portfolios[0] : null),
        [portfolioQuery.data]
    );

    // Extract pending trades
    const pendingTradesData = useMemo(() => portfolio?.trades?.filter((t) => t.status === 1) || [], [portfolio]);

    // Governance replacement targets: which existing trade(s) each pending entry will close on approval.
    const pendingReplacementsQuery = useAppQuery(
        {
            ...trpc.portfolioGovernance.getPendingReplacements.queryOptions({ portfolioId }),
            enabled: portfolioId > 0
        },
        { fallbackErrorMessage: 'Failed to load governance replacements' }
    );

    const replacementsByTradeId = useMemo(() => {
        const map = new Map<number, number[]>();

        for (const replacement of pendingReplacementsQuery.data?.replacements ?? []) {
            map.set(replacement.pendingTradeId, replacement.existingTradeIds);
        }

        return map;
    }, [pendingReplacementsQuery.data]);

    // Prepare entities to fetch
    const entitiesToFetch = useMemo(() => {
        if (!pendingTradesData.length) return [];

        // Use a Map to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<string, EntityObjectCompressed>();

        // Collect unique entities
        pendingTradesData.forEach((trade) => {
            const entityKey = `${trade.entityId}_${trade.entityType}`;

            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: trade.entityId,
                    t: trade.entityType === 'Asset' ? 'a' : trade.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }
        });

        // Convert Map values to an array
        return Array.from(entitiesMap.values());
    }, [pendingTradesData]);

    // Prepare strategies to fetch
    const strategiesToFetch = useMemo(() => {
        if (!pendingTradesData.length) return [];

        const strategiesSet = new Set<number>();

        pendingTradesData.forEach((trade) => {
            strategiesSet.add(trade.strategyId);
        });

        return Array.from(strategiesSet);
    }, [pendingTradesData]);

    // Fetch entities using the batched hook
    const { entities, isLoading: entitiesLoading } = useFetchEntities(entitiesToFetch);

    // Fetch strategies with tRPC
    const strategiesQuery = useAppQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({ ids: strategiesToFetch }),
        enabled: strategiesToFetch.length > 0
    });

    // Trade confirmation mutation
    const tradeConfirmationMutation = useTrpcMutation(
        trpc.user.getPendingBuyTradeConfirmation.mutationOptions({
            onSuccess: (data, variables) => {
                if (data?.confirmations) {
                    setTradeConfirmation({
                        tradeId: variables.id,
                        entryLogId: variables.entryLogId,
                        confirmations: data.confirmations
                    });

                    setIsConfirmModalOpen(true);
                }
            }
        }),
        {
            successMessage: '',
            fallbackErrorMessage: 'Failed to get trade confirmation'
        }
    );

    // Approve/reject trade mutation
    const approveRejectMutation = useTrpcMutation(
        trpc.user.approveRejectTrade.mutationOptions({
            onSuccess: (_data, variables) => {
                // Record the completed action in the overlay (applied to the derived rows). Not an effect.
                setActionCompleteById((prev) => ({
                    ...prev,
                    [variables.id]: variables.action === 'approve' ? 'approved' : 'rejected'
                }));
            }
        }),
        {
            successMessage: 'Trade processed successfully',
            fallbackErrorMessage: 'Failed to process trade action'
        }
    );

    const handleTradeAction = useCallback(
        async (tradeId: number, entryLogId: number, action: 'approve' | 'reject' | 'confirm') => {
            setActionInProgress((prev) => ({ ...prev, [tradeId]: true }));
            showSpinner();

            try {
                if (action === 'confirm') {
                    // Get confirmation first
                    await tradeConfirmationMutation.mutateAsync({
                        id: tradeId,
                        entryLogId
                    });
                } else {
                    // Approve or reject
                    await approveRejectMutation.mutateAsync({
                        id: tradeId,
                        action,
                        entryLogId
                    });
                }
            } catch (err) {
                console.error(`Error ${action}ing trade:`, err);
            } finally {
                setActionInProgress((prev) => ({ ...prev, [tradeId]: false }));
                hideSpinner();
            }
        },
        [tradeConfirmationMutation, approveRejectMutation]
    );

    // Table columns
    const columns = React.useMemo<ColumnDef<FormattedTrade>[]>(
        () => [
            {
                accessorKey: 'entity',
                header: 'Entity',
                minSize: 200,
                size: 300,
                maxSize: 500,
                cell: (info) => {
                    const value = info.getValue();

                    // If it's a React element, return it directly
                    if (typeof value !== 'string') {
                        return value;
                    }

                    return <div>{value}</div>;
                }
            },
            {
                accessorKey: 'entityType',
                header: 'Entity Type',
                minSize: 80,
                size: 120,
                maxSize: 180
            },
            {
                accessorKey: 'legs',
                header: 'Legs',
                minSize: 80,
                size: 125,
                maxSize: 180,
                cell: (info) => <div>{info.getValue() as React.ReactNode}</div>
            },
            {
                accessorKey: 'strategy',
                header: 'Strategy',
                minSize: 100,
                size: 150,
                maxSize: 250
            },
            {
                accessorKey: 'direction',
                header: 'Position side',
                minSize: 80,
                size: 100,
                maxSize: 150
            },
            {
                accessorKey: 'underlyingBias',
                header: 'Underlying exposure',
                minSize: 115,
                size: 145,
                maxSize: 190
            },
            {
                accessorKey: 'entryLog',
                header: 'Entry Log',
                cell: (info) => {
                    const value = info.getValue();

                    // If it's a React element, return it directly
                    if (typeof value !== 'string') {
                        return value;
                    }

                    return <div>{value}</div>;
                },
                minSize: 80,
                size: 100,
                maxSize: 150
            },
            {
                accessorKey: 'date',
                header: 'Date',
                minSize: 150,
                size: 200,
                maxSize: 250
            },
            {
                id: 'governance',
                header: 'Governance',
                minSize: 120,
                size: 170,
                maxSize: 240,
                cell: (info) => {
                    const { replaces } = info.row.original;

                    if (!replaces.length) {
                        return <span className="text-zinc-500">-</span>;
                    }

                    return (
                        <span className="text-xs text-amber-300" title="On approval, these trades are closed before this entry opens">
                            Replaces {replaces.map((tradeId) => `#${tradeId}`).join(', ')}
                        </span>
                    );
                }
            },
            {
                accessorKey: 'actions',
                minSize: 150,
                size: 220,
                maxSize: 300,
                header: 'Action',
                cell: (info) => {
                    const trade = info.row.original;

                    if (trade.actionComplete === 'approved') {
                        return <span>Approved</span>;
                    }

                    if (trade.actionComplete === 'rejected') {
                        return <span>Rejected</span>;
                    }

                    const isInProgress = actionInProgress[trade.id];

                    if (isInProgress) {
                        return (
                            <div className="flex justify-center">
                                <svg
                                    className="animate-spin h-5 w-5 text-primary"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path
                                        className="opacity-75"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="sr-only">Loading confirmation...</span>
                            </div>
                        );
                    }

                    const approvalText = portfolio?.tradingAccountId ? 'Confirm' : 'Approve';

                    return (
                        <div className="flex flex-row gap-2">
                            <Button
                                className="mr-2"
                                variant="primary"
                                onClick={async () =>
                                    handleTradeAction(trade.id, trade.entryLogId ?? 0, portfolio?.tradingAccountId ? 'confirm' : 'approve')
                                }
                            >
                                {approvalText}
                            </Button>
                            <DeleteButton
                                text="Reject"
                                onClick={async () => handleTradeAction(trade.id, trade.entryLogId ?? 0, 'reject')}
                            />
                        </div>
                    );
                }
            }
        ],
        [actionInProgress, portfolio, handleTradeAction]
    );

    // Derived rows: server data + entities + strategies + governance replacements + the action overlay.
    // Computed with useMemo (not an effect + setState).
    const pendingTrades = useMemo<FormattedTrade[]>(() => {
        if (!pendingTradesData.length || !entities.length || !strategiesQuery.data?.strategies) {
            return [];
        }

        return pendingTradesData.map((trade) => {
            const entity = entities.find((e) => e.id === trade.entityId && e.entityType === trade.entityType);
            const strategy = strategiesQuery.data.strategies.find((s) => s.id === trade.strategyId);

            const legsOutput =
                strategy?.legs && strategy.legs.length > 0 ? (
                    <div className="space-y-1">
                        {strategy.legs.map((leg, index) => (
                            <div key={`${trade.id}-leg-${index}`}>
                                {`${leg.number}. ${formatStrategyLegSemantics(leg)}${leg.description ? ` · ${leg.description}` : ''}`}
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="text-zinc-500">-</span>
                );

            return {
                id: trade.id,
                entityId: trade.entityId,
                entityType: trade.entityType,
                strategyId: trade.strategyId,
                direction: formatPositionSide(trade.direction),
                underlyingBias: formatUnderlyingBias(strategy ? summarizeLegUnderlyingBias(strategy.legs) : 'unknown'),
                entryLogId: trade.entryLogId,
                insertDateTime: trade.insertDateTime,
                entity: entity ? <EntityLink showName showSymbol entity={entity} /> : 'Unknown Entity',
                legs: legsOutput,
                strategy: strategy?.name || 'Unknown Strategy',
                entryLog: trade.entryLogId ? <EntryLogLink entryLogId={trade.entryLogId} /> : 'N/A',
                date: getDate({ date: trade.insertDateTime, format: 'readable1' }),
                actions: '',
                actionComplete: actionCompleteById[trade.id] ?? null,
                replaces: replacementsByTradeId.get(trade.id) ?? []
            };
        });
    }, [pendingTradesData, entities, strategiesQuery.data, replacementsByTradeId, actionCompleteById]);

    // Handle query errors
    useEffect(() => {
        if (strategiesQuery.error && strategiesToFetch.length > 0) {
            handleTrpcQuery(strategiesQuery, {
                fallbackErrorMessage: 'Failed to load strategies',
                setError: (msg) => setError(msg)
            });
        }
    }, [strategiesQuery.error, strategiesToFetch.length]);

    // Manage loading state
    const loading = useMemo(() => {
        return (
            portfolioQuery.isLoading ||
            (entitiesToFetch.length > 0 && entitiesLoading) ||
            (strategiesToFetch.length > 0 && strategiesQuery.isLoading)
        );
    }, [portfolioQuery.isLoading, entitiesLoading, strategiesQuery.isLoading, entitiesToFetch.length, strategiesToFetch.length]);

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (loading) {
            showSpinner();
        } else {
            hideSpinner();
        }
    }, [loading]);

    const handleConfirmModalApprove = () => {
        if (tradeConfirmation) {
            void handleTradeAction(tradeConfirmation.tradeId, tradeConfirmation.entryLogId, 'approve');
        }

        setIsConfirmModalOpen(false);
    };

    // An invalid portfolio id takes precedence over any query error (derived at render — not an effect).
    const displayError = portfolioId > 0 ? error : 'Invalid portfolio ID';

    const formatCurrency = useCallback((value: number | null) => {
        if (value === null) return '-';
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    const pageTitle =
        (portfolio?.name ? `${portfolio.name} - Pending Trades` : 'Pending Trades') +
        (!loading && displayError ? ' - Error - Project 314' : ' - Project 314');

    return (
        <UserLayout containerSize="1600px" title={pageTitle}>
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 mt-3">
                <div className="flex justify-between">
                    <div>
                        <h3 className="text-white mb-3 text-xl font-semibold">
                            {portfolio ? (
                                <>
                                    <Link className="text-primary hover:text-blue-400" to={`/user/portfolio/${portfolio.id}/`}>
                                        {portfolio.name}
                                    </Link>{' '}
                                    - Pending Trades
                                </>
                            ) : (
                                'Portfolio Pending Trades'
                            )}
                        </h3>
                    </div>
                </div>

                {displayError && (
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md" role="alert">
                        {displayError}
                    </div>
                )}

                {loading ? (
                    <DataLoadingSpinner />
                ) : (
                    <div className="mt-3 rounded-lg shadow-lg" id="portfolioPendingTradesContainer">
                        {pendingTrades.length > 0 ? (
                            <DataTable sort striped columns={columns} data={pendingTrades} search={pendingTrades.length > 15} />
                        ) : (
                            <div className="bg-blue-50 text-blue-800 border border-blue-100 p-4 mb-4 rounded-md">
                                No pending trades available for this portfolio.
                            </div>
                        )}
                    </div>
                )}

                {/* Confirmation Modal */}
                <Modal isOpen={isConfirmModalOpen} size="lg" title="Confirm Trade" onClose={() => setIsConfirmModalOpen(false)}>
                    <div className="text-zinc-400">
                        {tradeConfirmation && (
                            <>
                                <div className="space-y-3">
                                    {tradeConfirmation.confirmations.map((confirmation, index) => (
                                        <div
                                            className="rounded-md bg-zinc-900 p-4 text-white"
                                            key={`${tradeConfirmation.tradeId}-${index}`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <h4 className="text-sm font-semibold">Leg {index + 1}</h4>
                                                <span className="text-xs uppercase tracking-wide text-zinc-400">
                                                    {confirmation.brokerage ?? 'Local Estimate'}
                                                </span>
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <div className="text-zinc-400">Limit Price</div>
                                                    <div>{formatCurrency(confirmation.limitPrice)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-zinc-400">Estimated Cost</div>
                                                    <div>{formatCurrency(confirmation.estimatedCost)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-zinc-400">Commission</div>
                                                    <div>{formatCurrency(confirmation.estimatedCommission)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-zinc-400">Fees</div>
                                                    <div>{formatCurrency(confirmation.estimatedFees)}</div>
                                                </div>
                                            </div>
                                            {confirmation.summaryMessage ? (
                                                <p className="mt-3 text-sm text-zinc-400">{confirmation.summaryMessage}</p>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 text-center flex justify-center gap-2">
                                    <Button type="button" onClick={handleConfirmModalApprove}>
                                        Approve
                                    </Button>
                                    <Button type="button" onClick={() => setIsConfirmModalOpen(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            </div>
        </UserLayout>
    );
};

export default PortfolioPendingTrades;
